/**
 * Finding the button the assistant wants to point at.
 *
 * Every control the assistant knows about carries `data-assist="<id>"`, and the
 * same id is written down in `knowledge/appMap.ts`. That is the whole registry:
 * one attribute in the markup, one line in the map. No selectors in the map, no
 * component registration, nothing to keep in step by hand — if the attribute is
 * there the assistant can find it, and if it is not, the spotlight degrades to
 * words instead of breaking.
 *
 * The only real difficulty is *waiting*. "Show me how to add a photo" means
 * pressing the + and then pointing at a choice inside a sheet that does not
 * exist yet. So `waitForAnchor` watches the document until the element appears,
 * and gives up quietly after a moment rather than hanging.
 */

export const ATTR = 'data-assist';

/** The element for an anchor id, if it is on screen. */
export function findAnchor(id: string): HTMLElement | null {
  const found = document.querySelectorAll<HTMLElement>(`[${ATTR}="${CSS.escape(id)}"]`);
  if (!found.length) return null;
  // Two matches means one is inside a dialog that opened over the other (the
  // menu button also exists on a screen underneath). The last one in document
  // order is the one on top, which is the one a finger would reach.
  for (let i = found.length - 1; i >= 0; i--) {
    const el = found[i];
    if (el.isConnected && el.offsetParent !== null) return el;
  }
  return found[found.length - 1];
}

/**
 * Wait for an anchor to turn up — it may be behind a navigation or inside a
 * sheet that is still opening. Resolves with null rather than throwing, because
 * a step that cannot be pointed at is still a step that can be described.
 */
export function waitForAnchor(id: string, timeoutMs = 2500): Promise<HTMLElement | null> {
  const now = findAnchor(id);
  if (now) return Promise.resolve(now);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (el: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(el);
    };
    const observer = new MutationObserver(() => {
      const el = findAnchor(id);
      if (el) finish(el);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: [ATTR, 'class', 'style', 'open'] });
    const timer = setTimeout(() => finish(findAnchor(id)), timeoutMs);
  });
}

/** Bring an element into view before lighting it up, without a jolt. */
export function revealAnchor(el: HTMLElement, smooth: boolean): void {
  const box = el.getBoundingClientRect();
  const offScreen = box.top < 8 || box.bottom > window.innerHeight - 8 || box.left < 8 || box.right > window.innerWidth - 8;
  if (!offScreen) return;
  try {
    el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center', inline: 'center' });
  } catch {
    el.scrollIntoView();
  }
}

/** Every anchor currently on screen — used to tell the model what it can point at here. */
export function anchorsOnScreen(): string[] {
  return [...document.querySelectorAll<HTMLElement>(`[${ATTR}]`)]
    .filter((el) => el.offsetParent !== null)
    .map((el) => el.getAttribute(ATTR) ?? '')
    .filter(Boolean);
}
